import { Dispatch, SetStateAction } from "react";
import { IonLabel } from "@ionic/react";
import { type DateTime } from "luxon";
import { SchoolModel } from "model";
import { Link } from "react-router-dom";
import { createSchoolPath } from "routes/routeData";
import { Button, Colors, useFeatureFlags } from "swing-components";

import {
  isSameDate,
  TimesTableRowType,
  type TimesTableArrayType,
} from "~components/RequestForm/hooks";
import { SubScheduleDay } from "~components/RequestForm/utils";
import { translate } from "~utils";

import { useBulkEdit } from "./BulkEditTimes";
import { DayField } from "./DayField";
import { MultiDateField } from "./MultiDateField";

type RequestFormDateFieldProps = {
  /** The current datetime in the Schools timezone */
  now: DateTime;
  /** The School being edited */
  school: SchoolModel;
  /** The intervals for the Request */
  intervals: TimesTableArrayType;
  setIntervals: Dispatch<SetStateAction<TimesTableArrayType>>;

  /** The original end date of the request */
  originalEndDate?: DateTime;

  /*** MultiDateField ***/
  onDateChange: (params: { startDate?: DateTime; endDate?: DateTime }) => void;

  /*** DayField ***/
  /** Triggered when clicking on "Add Another Day" button. */
  onAddAnotherDay: () => void;

  /*** MultiDateTimeField ***/
  /** Multi Day **/
  /*
   * TODO: Currently the logic for time change is in the component, but we'll
   * want to move it upwards to the Request form.
   */
  onDateTimeChange: (newInterval: Array<TimesTableRowType>) => void;
  /** Long Term **/
  multiDayAdjustmentNotes: string;
  onMultiDayAdjustmentNotesChange: (notes: string) => void;
  /** Whether the field is disabled because the request is waitlisted or filled */
  isWaitlistedOrFilledReq?: boolean;
  /** The sub schedule for the request */
  subSchedule?: Array<SubScheduleDay>;
  /** Whether the field is disabled because the request is waitlisted or filled */
};

export function RequestFormDateField(props: RequestFormDateFieldProps) {
  /*** Props ***/
  // MultiDateField
  const { now, school, intervals, onDateChange } = props;
  // DayField
  const { onAddAnotherDay } = props;
  // MultiDateTimeField props
  const {
    // Multi Day
    onDateTimeChange,
    // Long Term
    multiDayAdjustmentNotes,
    onMultiDayAdjustmentNotesChange,
    isWaitlistedOrFilledReq,
    originalEndDate,
    subSchedule,
    setIntervals,
  } = props;

  const { openBulkEditsModal } = useBulkEdit({ school, setIntervals });

  const { flags } = useFeatureFlags();

  const showBulkEdits = intervals.length > 1 && !isWaitlistedOrFilledReq && flags.bulkEditTimes;

  const intervalsWithoutConflicts = intervals.filter(
    (interval) => !subSchedule?.some((date) => isSameDate(date.start, interval.startTime)),
  );

  const enabledDays = intervalsWithoutConflicts.filter((i) => i.isEnabled).length;

  /*** Hooks ***/
  return (
    <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Step #2.1: Select Dates */}
      <MultiDateField
        now={now}
        originalEndDate={originalEndDate}
        schoolHours={school.hours}
        schoolClosures={school.closures}
        start={getIntervalStartDateTime(intervals)}
        end={getIntervalEndDateTime(intervals)}
        onChange={onDateChange}
        isWaitlistedOrFilledReq={isWaitlistedOrFilledReq}
        subSchedule={subSchedule}
      />
      <div>
        <span>{translate("REQUEST_FORM_SELECT_DATE_SCHOOL_CLOSURE_1")}</span>
        <Link to={createSchoolPath(school.id)}>
          {translate("REQUEST_FORM_SELECT_DATE_SCHOOL_CLOSURE_2")}.
        </Link>
      </div>

      <div css={{ height: 1, background: Colors.slate200 }} />

      {/* Title */}

      <IonLabel className="font-medium">
        {translate("REQUEST_FORM_SELECT_DATE_DAY_COUNT", { count: enabledDays })}
      </IonLabel>

      {showBulkEdits && (
        <div css={{ display: "flex", justifyContent: "end" }}>
          <Button onClick={() => openBulkEditsModal()} type="tertiary">
            {translate("BULK_EDIT_TIMES_ENTRY")}
          </Button>
        </div>
      )}

      <DayField
        intervals={intervals}
        school={{ id: school.id, timezone: school.tzString }}
        originalEndDate={originalEndDate}
        additionalEdits={multiDayAdjustmentNotes}
        onIntervalChange={onDateTimeChange}
        onAddAnotherDay={onAddAnotherDay}
        onAdditionalEditsChange={onMultiDayAdjustmentNotesChange}
        isWaitlistedOrFilledReq={isWaitlistedOrFilledReq}
        subSchedule={subSchedule}
      />
    </div>
  );
}

/***** Helper Functions *****/
function getIntervalStartDateTime(intervals: TimesTableArrayType) {
  return intervals.filter((interval) => interval.isEnabled).at(0)!.startTime;
}

function getIntervalEndDateTime(intervals: TimesTableArrayType) {
  return intervals.filter((interval) => interval.isEnabled).at(-1)!.endTime;
}
